import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import {message, Modal, notification} from 'antd';
import {getJwt} from "./globalVariables";
import { v4 as uuidv4 } from 'uuid';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

// 全局变量存储 Modal
let loadingMessage: any = null;
let loadingModal: any = null;
let modalTimeout: any = null;

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {

  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {

      // 清理 loading & modal
      clearTimeout(modalTimeout);
      message.destroy();
      if (loadingModal) loadingModal.destroy();

      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const traceId = error.response.headers['traceid'] || '未知';
        const status = error.response.status;
        if (status >= 400 && status < 500) {
          // 4xx 错误，只显示 message 提示，不显示 notification
          message.error(`Response status: ${status}`);
          message.error(`错误提示：${error.response.data?.message}`, 10);
        } else {
          // 其他错误，显示 notification
          notification.open({
            message: `请求发送失败: ${status}`,
            description: `错误提示：${error.response.data?.message}\nTrace ID: ${traceId} \n 若持续发生请联系管理员`,
            duration: 20,
          });
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {

      // 生成 UUID
      const traceId = uuidv4();

      // 关闭残留的 loading
      message.destroy();
      if (loadingModal) loadingModal.destroy();

      // 立即显示 `message.loading`，不影响用户操作
      loadingMessage = message.loading({
        content: "Loading...",
        key: 'loading',
        duration: 0,
      });

      // **如果请求超过 2 秒未返回，显示全局遮罩**
      modalTimeout = setTimeout(() => {
        if (!loadingModal) {
          return;
        }
        loadingModal = Modal.info({
          title: '请求进行中',
          content: '请稍候，不要进行其他操作。\n如果长时间没响应请联系管理员，并附带该编号:' + traceId,
          centered: true,
          closable: false,
          maskClosable: false,
          zIndex: 9999,
          icon: null, // 去除默认 icon
          okText: '复制编号',
          onOk:()=>{
            // 将 traceId 复制到剪切板
            navigator.clipboard.writeText(traceId).then(() => {
              message.success('Trace ID 已复制到剪切板');
            }).catch(err => {
              message.error('复制 Trace ID 失败: ' + err);
            });
            return false;
          }
        });
      }, 1500);

      // 从 initialState 中获取 jwt，用作 Authorization 的头部
      const token = getJwt();

      // 如果有 token，则添加到请求头
      if (token) {
        config.headers = {
          ...config.headers, // 保留其他 headers
          Authorization: `Bearer ${token}`, // 使用 Bearer Token 认证
        };
      }

      // 添加 X-Trace-Id 到请求头
      config.headers = {
        ...config.headers,
        'X-Trace-Id': traceId,
      };

      // 拦截请求配置，进行个性化处理。
      // const url = config?.url?.concat('?token = 123');
      return { ...config };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {

      // 清理 loading & modal
      clearTimeout(modalTimeout);
      message.destroy();
      if (loadingModal) loadingModal.destroy();

      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      if (data?.success === false) {
        message.error('请求失败！');
      }
      return response;
    },
  ],
};
